import { useState } from 'react';

interface AIContent {
  mainQ?: string;
  mainA?: string;
}

interface AIBuyingGuideProps {
  content?: AIContent;
}

const repairGuides = [
  {
    title: 'Fix Broken Vertical Blind Slats',
    steps: ['Remove damaged vane from carrier', 'Match width and length of old vane', 'Insert new vane into carrier hook', 'Hang on track and test tilt'],
    part: 'Vertical Blind Vane Slats',
    icon: 'ri-layout-column-line',
  },
  {
    title: 'Replace Mini Blind Lift Cord',
    steps: ['Remove blind from window', 'Thread new cord through slat holes bottom-up', 'Tie secure knot at bottom', 'Thread through cord lock and test'],
    part: 'Mini Blind Lift Cord',
    icon: 'ri-line-height',
  },
  {
    title: 'Replace Mounting Bracket',
    steps: ['Unscrew old bracket from wall/frame', 'Match hole pattern and width', 'Install new bracket', 'Click headrail into bracket'],
    part: 'Inside/Outside Mount Brackets',
    icon: 'ri-tools-line',
  },
];

const faqItems = [
  {
    q: 'How do I find the right replacement part for my blinds?',
    a: 'Start by identifying the type of blind (vertical, mini, roller, etc.) and the specific component that is broken (cord, carrier, bracket, slat). Check for any brand markings on the headrail. Our category pages are organized by blind type, making it easy to locate the exact part. When in doubt, call us.',
  },
  {
    q: 'Do you carry parts for all blind brands?',
    a: 'We stock universal-fit replacement parts compatible with most major blind manufacturers including Levolor, Hunter Douglas, Bali, Graber, Springs Window Fashions, and generic brands. Most vertical blind parts and mini blind hardware are interchangeable across brands due to industry-standard sizing.',
  },
  {
    q: 'Can I fix my blinds myself with your parts?',
    a: 'Yes — most blind repairs are simple DIY projects requiring no special tools. Replacing a lift cord, swapping a broken slat, or snapping in a new carrier stem typically takes under 15 minutes. We include basic installation notes with all parts, and our team is available by phone for guidance.',
  },
];

const defaultMain = {
  mainQ: 'How Do I Find the Right Blind Replacement Part?',
  mainA: "Identifying the right blind part starts with knowing your blind type — vertical, horizontal mini blind, roller shade, or drapery. Then identify the broken component: cord, slat, carrier, bracket, or tilt mechanism. Most parts are universal-fit across major brands. Browse by category above or call our parts team for expert help matching your exact component.",
};

export default function AIBuyingGuide({ content }: AIBuyingGuideProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openRepair, setOpenRepair] = useState<number | null>(0);
  const { mainQ, mainA } = content || defaultMain;

  return (
    <section className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-amber-600 text-xs font-bold tracking-widest uppercase mb-3">
            Parts Finder &amp; Repair Guide
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-stone-900">AI-Powered Help Finding Your Part</h2>
          <p className="text-stone-500 mt-3 text-base max-w-xl mx-auto">
            Structured guidance for identifying, selecting, and installing the right replacement component.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left: Main Answer Block */}
          <div>
            <div className="bg-stone-50 rounded-lg border border-stone-200 p-8 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-sm">
                  <i className="ri-robot-line text-amber-700 text-sm" />
                </div>
                <span className="text-xs font-bold text-amber-700 tracking-wide uppercase">Parts Assistant</span>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-4 leading-snug">{mainQ || defaultMain.mainQ}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{mainA || defaultMain.mainA}</p>
            </div>

            {/* Repair Guides */}
            <div className="space-y-3">
              <h3 className="font-bold text-stone-800 text-base mb-4">Step-by-Step Repair Guides</h3>
              {repairGuides.map((guide, idx) => (
                <div key={guide.title} className="border border-stone-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenRepair(openRepair === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-white hover:bg-stone-50 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-sm flex-shrink-0">
                        <i className={`${guide.icon} text-amber-600 text-sm`} />
                      </div>
                      <span className="font-semibold text-stone-800 text-sm">{guide.title}</span>
                    </div>
                    <i className={`text-stone-400 text-base flex-shrink-0 transition-transform duration-200 ${openRepair === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </button>
                  {openRepair === idx && (
                    <div className="px-5 pb-5 bg-stone-50 border-t border-stone-100">
                      <ol className="space-y-2 mt-4">
                        {guide.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-stone-600">
                            <span className="w-5 h-5 bg-amber-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              {i + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                      <div className="mt-4 pt-4 border-t border-stone-200">
                        <span className="text-xs text-stone-500">Part needed: </span>
                        <span className="text-xs font-semibold text-amber-700">{guide.part}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: FAQ */}
          <div>
            <h3 className="font-bold text-stone-800 text-base mb-5">Common Parts Questions</h3>
            <div className="space-y-3 mb-8">
              {faqItems.map((item, idx) => (
                <div key={idx} className="border border-stone-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-start justify-between gap-4 px-5 py-4 bg-white hover:bg-stone-50 transition-colors cursor-pointer text-left"
                  >
                    <span className="font-semibold text-stone-800 text-sm leading-snug pr-2">{item.q}</span>
                    <i className={`text-stone-400 text-lg flex-shrink-0 transition-transform duration-200 mt-0.5 ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5 bg-stone-50 border-t border-stone-100">
                      <p className="text-stone-600 text-sm leading-relaxed mt-3">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Part Type Quick Reference */}
            <div className="bg-stone-900 rounded-lg p-6">
              <h4 className="font-bold text-white text-sm mb-4">Quick Part Type Reference</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { type: 'Broken slat/vane', part: 'Replacement Slat' },
                  { type: 'Won\'t tilt', part: 'Tilt Mechanism / Wand' },
                  { type: 'Won\'t lift/lower', part: 'Lift Cord + Lock' },
                  { type: 'Falls off track', part: 'Carrier / Stem' },
                  { type: 'Loose from wall', part: 'Mounting Bracket' },
                  { type: 'Broken cord loop', part: 'Bead Chain / Cord' },
                ].map((item) => (
                  <div key={item.type} className="bg-stone-800 rounded-sm px-3 py-2.5">
                    <div className="text-stone-400 text-[10px] uppercase tracking-wide mb-1">Problem</div>
                    <div className="text-white text-xs font-medium leading-snug">{item.type}</div>
                    <div className="text-amber-400 text-[11px] mt-1.5 font-semibold">→ {item.part}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
